/**
 * JSONL log of every channel turn (grade / classify) — one entry per line.
 *
 * Written by `submitToChannel` after each request completes (success or fail).
 * Read by `synkro local-cc logs` to show recent activity.
 *
 * Best-effort: any I/O error here is swallowed so logging never breaks the
 * actual grading path.
 */

import { appendFileSync, existsSync, mkdirSync, openSync, readFileSync, readSync, closeSync, statSync, watchFile, unwatchFile } from 'node:fs';
import { dirname, join } from 'node:path';
import { homedir } from 'node:os';
import type { LocalCCRole } from './prompts.js';

export const TURN_LOG_PATH = join(homedir(), '.synkro', 'cc_sessions', 'turns.log');

export type TurnStatus = 'ok' | 'timeout' | 'error';

export interface TurnEntry {
  /** ISO timestamp of when the request started. */
  ts: string;
  role: LocalCCRole;
  /** Wall-clock ms from submit to result/error. */
  duration_ms: number;
  status: TurnStatus;
  /** Truncated request payload (first ~400 chars of caller-supplied text). */
  request_preview: string;
  /** Truncated response result (first ~400 chars). Empty on non-ok. */
  response_preview: string;
  /** Parsed severity if we can extract one from the verdict. Optional. */
  severity?: string;
  /** Error message if status is timeout/error. */
  error?: string;
}

const PREVIEW_MAX = 400;

function truncate(s: string, max = PREVIEW_MAX): string {
  if (s.length <= max) return s;
  return s.slice(0, max) + '… [+' + (s.length - max) + ' chars]';
}

/** Best-effort extraction of severity from the result text. */
function extractSeverity(result: string): string | undefined {
  // <synkro-verdict>{"severity":"audit",…}</synkro-verdict>
  const m = result.match(/<synkro-(?:verdict|intent)>([\s\S]*?)<\/synkro-(?:verdict|intent)>/);
  if (!m) return undefined;
  try {
    const obj = JSON.parse(m[1]) as { severity?: string; verdict?: string; type?: string; ok?: boolean };
    if (obj.severity) return String(obj.severity);
    if (typeof obj.ok === 'boolean') return obj.ok ? 'ok' : 'violations';
    if (obj.type) return String(obj.type);
    if (obj.verdict) return String(obj.verdict);
  } catch {
    // not parseable JSON inside the tag — ignore
  }
  return undefined;
}

/** Append one turn to the JSONL log. Best-effort — never throws. */
export function appendTurn(args: {
  startedAt: number;
  role: LocalCCRole;
  request: string;
  result?: string;
  status: TurnStatus;
  error?: string;
}): void {
  try {
    mkdirSync(dirname(TURN_LOG_PATH), { recursive: true });
    const entry: TurnEntry = {
      ts: new Date(args.startedAt).toISOString(),
      role: args.role,
      duration_ms: Date.now() - args.startedAt,
      status: args.status,
      request_preview: truncate(args.request),
      response_preview: args.result ? truncate(args.result) : '',
      severity: args.result ? extractSeverity(args.result) : undefined,
      error: args.error,
    };
    appendFileSync(TURN_LOG_PATH, JSON.stringify(entry) + '\n', 'utf-8');
  } catch {
    // never let logging fail the call site
  }
}

/**
 * Read the most recent N turn entries (newest first). Returns an empty array
 * on any error.
 */
export function readRecentTurns(n = 20): TurnEntry[] {
  if (!existsSync(TURN_LOG_PATH)) return [];
  try {
    // For typical sizes this is fine; if the file grows huge we'd switch to
    // tail-style reverse reading. Cap by stat size for safety.
    const size = statSync(TURN_LOG_PATH).size;
    if (size === 0) return [];
    const text = readFileSync(TURN_LOG_PATH, 'utf-8');
    const lines = text.split('\n').filter(Boolean);
    const lastN = lines.slice(-n).reverse();
    return lastN
      .map(line => {
        try { return JSON.parse(line) as TurnEntry; } catch { return null; }
      })
      .filter((x): x is TurnEntry => x !== null);
  } catch {
    return [];
  }
}

/**
 * Tail the turn log: invoke `onEntry` for each new entry appended after the
 * call. Handles file truncation/rotation by re-syncing offset to 0 when size
 * shrinks. Returns a `stop()` to detach the watcher.
 */
export function followTurns(onEntry: (t: TurnEntry) => void): () => void {
  // Make sure the file exists so watchFile has something to poll. Creating
  // an empty file is harmless if it already exists.
  try {
    mkdirSync(dirname(TURN_LOG_PATH), { recursive: true });
    if (!existsSync(TURN_LOG_PATH)) {
      appendFileSync(TURN_LOG_PATH, '', 'utf-8');
    }
  } catch {
    // best-effort
  }

  let lastSize = (() => {
    try { return statSync(TURN_LOG_PATH).size; } catch { return 0; }
  })();
  let pendingPartial = ''; // bytes after the last \n if a write split on a line boundary

  const drainNewBytes = (from: number, to: number): void => {
    if (to <= from) return;
    let fd: number | null = null;
    try {
      fd = openSync(TURN_LOG_PATH, 'r');
      const len = to - from;
      const buf = Buffer.alloc(len);
      readSync(fd, buf, 0, len, from);
      const text = pendingPartial + buf.toString('utf-8');
      const lastNewline = text.lastIndexOf('\n');
      if (lastNewline === -1) {
        pendingPartial = text;
        return;
      }
      const complete = text.slice(0, lastNewline);
      pendingPartial = text.slice(lastNewline + 1);
      for (const line of complete.split('\n')) {
        if (!line) continue;
        try {
          onEntry(JSON.parse(line) as TurnEntry);
        } catch {
          // skip malformed lines
        }
      }
    } catch {
      // best-effort
    } finally {
      if (fd !== null) {
        try { closeSync(fd); } catch { /* noop */ }
      }
    }
  };

  // Poll-based watcher (cross-platform reliable). 250ms is responsive
  // enough for tail-f UX without burning CPU.
  watchFile(TURN_LOG_PATH, { interval: 250 }, (curr, prev) => {
    if (curr.size < lastSize) {
      // file truncated/rotated — reset.
      lastSize = 0;
      pendingPartial = '';
    }
    if (curr.size > lastSize) {
      drainNewBytes(lastSize, curr.size);
      lastSize = curr.size;
    }
  });

  return () => unwatchFile(TURN_LOG_PATH);
}
